/**
 * Same-tab change notification for values mirrored in `localStorage`.
 *
 * The browser only fires `storage` in *other* tabs, so a writer has to tell the
 * subscribers in its own tab itself. Synthesizing a `StorageEvent` to do that
 * carries a `newValue`/`oldValue` payload no subscriber reads — they re-read
 * `localStorage` and only need to know which key moved — and CodeQL flags the
 * synthesized constructor call as superfluous arguments. Announcing just the
 * key on a dedicated event drops the unread payload and leaves the native
 * `storage` event to do what it actually does: cross-tab.
 */
const SAME_TAB_STORAGE_EVENT = 'copilot-task-composer:storage'

export function notifyStorageKeyChanged(key: string) {
  window.dispatchEvent(new CustomEvent(SAME_TAB_STORAGE_EVENT, {detail: key}))
}

/** Calls back whenever `key` changes in this tab or another one. */
export function subscribeToStorageKey(key: string, callback: () => void) {
  const handleCrossTab = (event: StorageEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === key) {
      callback()
    }
  }
  const handleSameTab = (event: Event) => {
    if ((event as CustomEvent<string>).detail === key) {
      callback()
    }
  }
  window.addEventListener('storage', handleCrossTab)
  window.addEventListener(SAME_TAB_STORAGE_EVENT, handleSameTab)
  return () => {
    window.removeEventListener('storage', handleCrossTab)
    window.removeEventListener(SAME_TAB_STORAGE_EVENT, handleSameTab)
  }
}
